/**
 * Settings API Client
 *
 * API functions for site settings management (UTM configuration)
 */

import { autoRefreshToken } from './auth'

// Use relative URLs by default (Vite proxy handles routing to backend)
// For production builds, set VITE_API_BASE_URL to the deployed backend base path.
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';

/**
 * Get JWT Bearer token for API authentication
 * Auto-refreshes token if expired
 */
const getAuthHeader = async (): Promise<string> => {
  const accessToken = await autoRefreshToken()

  if (!accessToken) {
    throw new Error('Not authenticated')
  }

  return `Bearer ${accessToken}`
};

// ========================================
// Types
// ========================================

export interface UTMConfig {
  enabled: boolean;
  domains: string[];
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
}

export interface UTMConfigResponse {
  site_code: string;
  config: UTMConfig;
  updated_at?: string;
}

export interface AllUTMConfigsResponse {
  configs: Record<string, UTMConfig>;
}

export interface UTMTestRequest {
  text: string;
  site_code: string;
}

export interface UTMTestResponse {
  original: string;
  transformed: string;
  urls_modified: number;
}

// Valid site codes
export const VALID_SITE_CODES = ['ua', 'ge', 'wp', 'adm', 'ru'] as const;
export type SiteCode = typeof VALID_SITE_CODES[number];

// Site code display names
export const SITE_CODE_NAMES: Record<SiteCode, string> = {
  ua: 'UA (Україна)',
  ge: 'GE (Georgia)',
  wp: 'WP (WordPress)',
  adm: 'ADM (Admin)',
  ru: 'RU (Россия)',
};

// Default UTM config
export const DEFAULT_UTM_CONFIG: UTMConfig = {
  enabled: false,
  domains: [],
  utm_source: '',
  utm_medium: '',
  utm_campaign: '',
  utm_content: '',
  utm_term: '',
};

// ========================================
// API Functions
// ========================================

/**
 * Get UTM settings for a specific site
 */
export async function getUTMSettings(siteCode: string): Promise<UTMConfigResponse> {
  const authHeader = await getAuthHeader()
  const response = await fetch(`${API_BASE}/admin/settings/${siteCode}/utm`, {
    headers: {
      'Authorization': authHeader,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get UTM settings: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update UTM settings for a specific site
 */
export async function updateUTMSettings(siteCode: string, config: UTMConfig): Promise<UTMConfigResponse> {
  const authHeader = await getAuthHeader()
  const response = await fetch(`${API_BASE}/admin/settings/${siteCode}/utm`, {
    method: 'PUT',
    headers: {
      'Authorization': authHeader,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(config),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to update UTM settings: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get UTM settings for all sites
 */
export async function getAllUTMSettings(): Promise<AllUTMConfigsResponse> {
  const authHeader = await getAuthHeader()
  const response = await fetch(`${API_BASE}/admin/settings/utm/all`, {
    headers: {
      'Authorization': authHeader,
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to get all UTM settings: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Test UTM transformation on sample text
 */
export async function testUTMTransformation(request: UTMTestRequest): Promise<UTMTestResponse> {
  const authHeader = await getAuthHeader()
  const response = await fetch(`${API_BASE}/admin/settings/utm/test`, {
    method: 'POST',
    headers: {
      'Authorization': authHeader,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.detail || `Failed to test UTM transformation: ${response.statusText}`);
  }

  return response.json();
}
